import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronRight, 
  ChevronLeft, 
  Check,
  Link2,
  Music,
  AlertCircle,
  ExternalLink,
  Mail,
  Plane,
  Camera
} from 'lucide-react';
import MusicPicker from '../components/MusicPicker';

const NewOrderForm = ({ subscription, onSubmit, onCancel, userEmail, isFR = false }) => {
  const t = (en, fr) => (isFR ? fr : en);
  const [currentStep, setCurrentStep] = useState(1);
  const stepTitleRef = useRef(null);
  const [validationErrors, setValidationErrors] = useState([]);
  
  const hasDroneSubscription = subscription?.with_drone === true;
  const [hasDroneExtraCredit, setHasDroneExtraCredit] = useState(false);
  
  const [formData, setFormData] = useState({
    deliveryEmail: userEmail || '', 
    propertyName: '',
    matterportUrl: '',
    isResidential: null,
    hasLogoAnimation: null,
    agentLogoLink: '',
    logoAnimationType: '',
    
    includeDrone: hasDroneSubscription ? 'yes' : 'no',

    walkthroughIntroType: null,
    walkthroughIntroPhotoLink: '',
    walkthroughFlyoverAddress: '',
    walkthroughFlyoverType: '',
    droneClipsLink: '',
    hasTitle: null,
    titleText: '',
    hasMusic: null,
    musicTrack: '',
    
    // ✅ 360° Showcase - Template toggle
    showcaseApplyTemplate: null,
    showcaseLabel: '',
    showcasePrice: '',
    showcaseAgentPhoto: '',
    showcaseAgentName: '',
    showcaseAgentPhone: '',
    showcaseAddress: '',
    showcaseCity: '',
    showcaseState: '',
    showcasePostalCode: '',
    showcaseBedrooms: '',
    showcaseBathrooms: '',
    showcaseSqFt: '',
    showcaseHasMusic: null,
    showcaseMusicTrack: '',
    
    premiumPhotosLink: '',
    premiumPhotosNumbered: null,
    premiumSlideshowTransition: '',
    premiumHasMusic: null,
    premiumMusicTrack: '',
    
    // ✅ Short Success Slideshow - Template toggle + fields
    shortApplyTemplate: null,
    shortLabel: 'sold', // Always SOLD
    shortPrice: '',
    shortAgentPhoto: '',
    shortAgentName: '',
    shortAgentPhone: '',
    shortAddress: '',
    shortCity: '',
    shortState: '',
    shortPostalCode: '',
    shortBedrooms: '',
    shortBathrooms: '',
    shortSqFt: '',
    shortPhotosLink: '',
    shortPhotosNumbered: null,
    shortSlideshowTransition: '',
    shortHasMusic: null,
    shortMusicTrack: '',
    
    portfolioPermission: null,
    notes: '',
    extraFeedback: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const shouldShowDroneClips = hasDroneSubscription || hasDroneExtraCredit || formData.includeDrone === 'yes';

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (stepTitleRef.current) {
      // Logic scroll if needed
    }
  }, [currentStep]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setHasDroneExtraCredit(localStorage.getItem('pendingDroneExtraCredit') === 'true');
    }
  }, []);

  const isFormDisabled = formData.isResidential === 'no';

  const validateStep = (step) => {
    const errors = [];
    
    switch(step) {
      case 1:
        if (!formData.isResidential) errors.push(t("Please indicate if this is a residential property", "Indiquez si c'est une propriété résidentielle"));
        if (formData.isResidential === 'yes') {
          if (!formData.propertyName.trim()) errors.push(t("Property name is required", "Le nom de la propriété est requis"));
          if (!formData.matterportUrl.trim()) errors.push(t("Matterport link is required", "Le lien Matterport est requis"));
          if (shouldShowDroneClips && !formData.droneClipsLink.trim()) {
            errors.push(t("Drone clips link is required", "Le lien des clips drone est requis"));
          }
        }
        break;
        
      case 2:
        if (!formData.hasLogoAnimation) errors.push(t("Please choose if you want a logo animation", "Choisissez si vous souhaitez une animation de logo"));
        if (formData.hasLogoAnimation === 'yes') {
          if (!formData.agentLogoLink.trim()) errors.push(t("Logo link is required", "Le lien du logo est requis"));
          if (!formData.logoAnimationType) errors.push(t("Logo animation type is required", "Le type d'animation de logo est requis"));
        }
        
        if (!shouldShowDroneClips && !isFR) {
          // Intro vidéo uniquement pour la version EN (le bloc est masqué en FR)
          if (!formData.walkthroughIntroType) errors.push(t("Video introduction type is required", "Le type d'intro vidéo est requis"));
          if (formData.walkthroughIntroType === 'photo' && !formData.walkthroughIntroPhotoLink.trim()) {
            errors.push(t("Exterior photo link is required", "Le lien de la photo extérieure est requis"));
          }
          if (formData.walkthroughIntroType === 'flyover') {
            if (!formData.walkthroughFlyoverAddress.trim()) errors.push(t("Property address is required for flyover", "L'adresse est requise pour le flyover"));
            if (!formData.walkthroughFlyoverType) errors.push(t("Flyover style is required", "Le style de flyover est requis"));
          }
        }
        
        if (!formData.hasTitle) errors.push(t("Please choose if you want a title", "Choisissez si vous voulez un titre"));
        if (formData.hasTitle === 'yes' && !formData.titleText.trim()) {
          errors.push(t("Title text is required", "Le texte du titre est requis"));
        }
        
        if (!formData.hasMusic) errors.push(t("Please choose if you want music", "Choisissez si vous voulez de la musique"));
        if (formData.hasMusic === 'yes' && !formData.musicTrack) {
          errors.push(t("Please select a music track", "Veuillez sélectionner une musique"));
        }
        break;
        
      case 3:
        if (!formData.showcaseApplyTemplate) errors.push(t("Please indicate if you want to apply a template", "Indiquez si vous voulez appliquer un modèle"));
        if (formData.showcaseApplyTemplate === 'yes') {
          if (!formData.showcaseLabel) errors.push(t("Label is required", "Le libellé est requis"));
          if (!formData.showcasePrice.trim()) errors.push(t("Price is required", "Le prix est requis"));
          if (!formData.showcaseAgentPhoto.trim()) errors.push(t("Agent photo is required", "La photo de l'agent est requise"));
          if (!formData.showcaseAgentName.trim()) errors.push(t("Agent name is required", "Le nom de l'agent est requis"));
          if (!formData.showcaseAgentPhone.trim()) errors.push(t("Agent phone is required", "Le téléphone de l'agent est requis"));
          if (!formData.showcaseAddress.trim()) errors.push(t("Address is required", "L'adresse est requise"));
          if (!formData.showcaseCity.trim()) errors.push(t("City is required", "La ville est requise"));
          if (!formData.showcaseState.trim()) errors.push(t("State is required", "La région est requise"));
          if (!formData.showcasePostalCode.trim()) errors.push(t("ZIP code is required", "Le code postal est requis"));
          if (!formData.showcaseBedrooms) errors.push(t("Number of bedrooms is required", "Le nombre de chambres est requis"));
          if (!formData.showcaseBathrooms) errors.push(t("Number of bathrooms is required", "Le nombre de salles de bain est requis"));
          if (!formData.showcaseSqFt) errors.push(t("Square footage is required", "La surface est requise"));
        }
        if (!formData.showcaseHasMusic) errors.push(t("Please choose if you want music", "Choisissez si vous voulez de la musique"));
        if (formData.showcaseHasMusic === 'yes' && !formData.showcaseMusicTrack) {
          errors.push(t("Please select a music track", "Veuillez sélectionner une musique"));
        }
        break;
        
      case 4:
        if (!formData.premiumPhotosLink.trim()) errors.push(t("Photos link is required", "Le lien des photos est requis"));
        if (!formData.premiumPhotosNumbered) errors.push(t("Please indicate if photos are numbered", "Indiquez si les photos sont numérotées"));
        if (formData.premiumPhotosNumbered === 'yes') {
          if (!formData.premiumSlideshowTransition) errors.push(t("Transition style is required", "Le style de transition est requis"));
          if (!formData.premiumHasMusic) errors.push(t("Please choose if you want music", "Choisissez si vous voulez de la musique"));
          if (formData.premiumHasMusic === 'yes' && !formData.premiumMusicTrack) {
            errors.push(t("Please select a music track", "Veuillez sélectionner une musique"));
          }
        }
        break;
        
      case 5:
        if (!formData.shortPhotosLink.trim()) errors.push(t("Photos link is required", "Le lien des photos est requis"));
        if (!formData.shortPhotosNumbered) errors.push(t("Please indicate if photos are numbered", "Indiquez si les photos sont numérotées"));
        if (!formData.shortApplyTemplate) errors.push(t("Please indicate if you want to apply a template", "Indiquez si vous voulez appliquer un modèle"));
        if (formData.shortApplyTemplate === 'yes') {
          if (!formData.shortPrice.trim()) errors.push(t("Price is required", "Le prix est requis"));
          if (!formData.shortAgentPhoto.trim()) errors.push(t("Agent photo is required", "La photo de l'agent est requise"));
          if (!formData.shortAgentName.trim()) errors.push(t("Agent name is required", "Le nom de l'agent est requis"));
          if (!formData.shortAgentPhone.trim()) errors.push(t("Agent phone is required", "Le téléphone de l'agent est requis"));
          if (!formData.shortAddress.trim()) errors.push(t("Address is required", "L'adresse est requise"));
          if (!formData.shortCity.trim()) errors.push(t("City is required", "La ville est requise"));
          if (!formData.shortState.trim()) errors.push(t("State is required", "La région est requise"));
          if (!formData.shortPostalCode.trim()) errors.push(t("ZIP code is required", "Le code postal est requis"));
          if (!formData.shortBedrooms) errors.push(t("Number of bedrooms is required", "Le nombre de chambres est requis"));
          if (!formData.shortBathrooms) errors.push(t("Number of bathrooms is required", "Le nombre de salles de bain est requis"));
          if (!formData.shortSqFt) errors.push(t("Square footage is required", "La surface est requise"));
        }
        if (formData.shortPhotosNumbered === 'yes') {
          if (!formData.shortSlideshowTransition) errors.push(t("Transition style is required", "Le style de transition est requis"));
          if (!formData.shortHasMusic) errors.push(t("Please choose if you want music", "Choisissez si vous voulez de la musique"));
          if (formData.shortHasMusic === 'yes' && !formData.shortMusicTrack) {
            errors.push(t("Please select a music track", "Veuillez sélectionner une musique"));
          }
        }
        break;
        
      case 6:
        if (!formData.portfolioPermission) errors.push(t("Portfolio permission is required", "L'autorisation portfolio est requise"));
        if (!formData.deliveryEmail.trim()) errors.push(t("Delivery email address is required", "L'adresse email de livraison est requise"));
        break;
    }
    
    return errors;
  };

  const renderValidationErrors = () => {
    if (validationErrors.length === 0) return null;
    return (
      <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 md:p-6 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-manrope font-bold text-red-800 mb-2 text-sm md:text-base">{t("Please complete all required fields:", "Veuillez remplir tous les champs obligatoires :")}</h3>
            <ul className="space-y-1">
              {validationErrors.map((error, index) => (<li key={index} className="text-xs md:text-sm text-red-700">• {error}</li>))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // --- STEPS RENDERERS ---

  const renderStep1 = () => (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h2 className="font-manrope text-2xl md:text-3xl font-bold text-gray-900 mb-3 animate-fade-in-up">
          {t("Property Information", "Informations sur la propriété")}
        </h2>
        <p className="text-base md:text-lg text-gray-600">
          {t("Let's start with the basics", "Commençons par l'essentiel")}
        </p>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-base font-semibold text-gray-700 mb-3">
            {t(
              "Is your Matterport link for a residential real estate listing? *",
              "Votre lien Matterport concerne-t-il un bien résidentiel ? *"
            )}
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => updateFormData('isResidential', 'yes')} className={`flex-1 py-3 px-4 rounded-full border-2 font-medium transition-all ${formData.isResidential === 'yes' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>{t("Yes", "Oui")}</button>
            <button onClick={() => updateFormData('isResidential', 'no')} className={`flex-1 py-3 px-4 rounded-full border-2 font-medium transition-all ${formData.isResidential === 'no' ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 hover:border-gray-300'}`}>{t("No", "Non")}</button>
          </div>
          {formData.isResidential === 'no' && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-xs md:text-sm text-red-600">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="font-semibold">{t("This service is currently only available for residential properties.", "Ce service est actuellement réservé aux propriétés résidentielles.")}</span>
              </div>
            </div>
          )}
        </div>
        <div className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <label className="block text-base font-semibold text-gray-700 mb-2">
            {t("Property Name *", "Nom de la propriété *")}
          </label>
          <input
            type="text"
            value={formData.propertyName}
            onChange={(e) => updateFormData('propertyName', e.target.value)}
            disabled={isFormDisabled}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none disabled:bg-gray-100 text-sm md:text-base"
            placeholder={t("e.g., 123 Ocean View Drive", "ex. 123 Rue de la Paix")}
          />
        </div>
        <div className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <label className="block text-base font-semibold text-gray-700 mb-2">{t("Matterport Link *", "Lien Matterport *")}</label>
          <div className="relative">
            <Link2 className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <input type="url" value={formData.matterportUrl} onChange={(e) => updateFormData('matterportUrl', e.target.value)} disabled={isFormDisabled} className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none disabled:bg-gray-100 text-sm md:text-base" placeholder="https://my.matterport.com/show/?m=..." />
          </div>
        </div>

        {shouldShowDroneClips && (
          <div className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
            <label className="block text-base font-semibold text-gray-700 mb-2">{t("Drone Clips Link *", "Lien des clips drone *")}</label>
            <div className="relative">
              <Link2 className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="url"
                value={formData.droneClipsLink}
                onChange={(e) => updateFormData('droneClipsLink', e.target.value)}
                disabled={isFormDisabled}
                className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none disabled:bg-gray-100 text-sm md:text-base"
                placeholder={t("Paste link (Dropbox, Google Drive...)", "Collez le lien (Dropbox, Google Drive...)")}
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">{t("Please provide a public link to download your drone clips.", "Fournissez un lien public pour télécharger vos clips drone.")}</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className={`space-y-4 md:space-y-6 ${isFormDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4 mb-4">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center bg-[#3C59B2] flex-shrink-0">
          <span className="text-xl md:text-2xl font-bold text-white">2</span>
        </div>
        <div className="flex-1">
          <h2 className="font-manrope text-2xl md:text-3xl font-bold text-gray-900">
            {t("Configure your video", "Composition de la vidéo")}
          </h2>
        </div>
      </div>
      
      <div className="space-y-4 md:space-y-6">

        {!hasDroneSubscription && !isFR && (
          <div className="animate-fade-in space-y-4">
            <div className="bg-gray-50 p-4 rounded-xl border-2 border-gray-200">
              <label className="block text-sm font-semibold text-gray-700 mb-3">{t("Video Introduction *", "Introduction vidéo *")}</label>
              <p className="text-xs text-gray-600 mb-3">{t("Choose how to start your walkthrough video.", "Choisissez comment démarrer votre vidéo visite.")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <button onClick={() => updateFormData('walkthroughIntroType', 'photo')} className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${formData.walkthroughIntroType === 'photo' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>{t("Exterior Photo", "Photo extérieure")}</button>
                <button onClick={() => updateFormData('walkthroughIntroType', 'flyover')} className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${formData.walkthroughIntroType === 'flyover' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>{t("Google Flyover", "Vol Google")}</button>
                <button onClick={() => updateFormData('walkthroughIntroType', 'none')} className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${formData.walkthroughIntroType === 'none' ? 'border-gray-500 bg-gray-50 text-gray-700' : 'border-gray-200 hover:border-gray-300'}`}>{t("None", "Aucun")}</button>
              </div>
            </div>
            {formData.walkthroughIntroType === 'photo' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t("Exterior Photo Link *", "Lien photo extérieure *")}</label>
                <div className="relative">
                  <Link2 className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                  <input type="url" value={formData.walkthroughIntroPhotoLink} onChange={(e) => updateFormData('walkthroughIntroPhotoLink', e.target.value)} className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder={t("Paste exterior photo link", "Collez le lien de la photo extérieure")} />
                </div>
              </div>
            )}
            {formData.walkthroughIntroType === 'flyover' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t("Property Address *", "Adresse du bien *")}</label>
                  <input
                    type="text"
                    value={formData.walkthroughFlyoverAddress}
                    onChange={(e) => updateFormData('walkthroughFlyoverAddress', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base"
                    placeholder={t("e.g., 123 Main Street, Los Angeles", "ex. 123 rue Principale, Paris")}
                  />
                  <p className="mt-2 text-xs md:text-sm text-amber-700 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      3D Data Availability: Flyover effects require Google Maps 3D coverage. You can check 3D availability by toggling
                      &nbsp;<span className="font-semibold">3D mode</span>&nbsp;on Google Maps for this location.
                    </span>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">{t("Choose Flyover Style *", "Choisissez le style de vol *")}</label>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {[{id: 'fly-to-orbit', name: t('Fly-To and Orbit', 'Vol puis orbite'), vid: 'SePOrUghRNU'}, {id: 'orbit', name: t('Orbit', 'Orbite'), vid: 'z0Z-rICpdmk'}, {id: 'spiral', name: t('Spiral', 'Spirale'), vid: 'gaY5FwfBgn0'}].map(style => (
                      <button key={style.id} onClick={() => updateFormData('walkthroughFlyoverType', style.id)} className={`p-3 md:p-4 rounded-xl border-2 transition-all ${formData.walkthroughFlyoverType === style.id ? 'border-[#3C59B2] bg-blue-50 shadow-lg' : 'border-gray-200 hover:border-gray-300'}`}>
                        <div className={`font-bold text-sm md:text-base mb-2 py-2 px-4 rounded-lg ${formData.walkthroughFlyoverType === style.id ? 'bg-[#3C59B2] text-white' : 'bg-gray-100 text-gray-900'}`}>{style.name}</div>
                        <div className="w-full aspect-video rounded-lg overflow-hidden"><iframe className="w-full h-full" src={`https://www.youtube.com/embed/${style.vid}`} frameBorder="0" allowFullScreen></iframe></div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        <div>
          <label className="block text-base font-semibold text-gray-700 mb-3">{t("Would you like a logo animation? *", "Souhaitez-vous une animation de logo ? *")}</label>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <button onClick={() => updateFormData('hasLogoAnimation', 'yes')} className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${formData.hasLogoAnimation === 'yes' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>{t("Yes", "Oui")}</button>
            <button onClick={() => updateFormData('hasLogoAnimation', 'no')} className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${formData.hasLogoAnimation === 'no' ? 'border-gray-500 bg-gray-50 text-gray-700' : 'border-gray-200 hover:border-gray-300'}`}>{t("No", "Non")}</button>
          </div>
          {formData.hasLogoAnimation === 'yes' && (
            <div className="space-y-4">
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">{t("Logo Link *", "Lien du logo *")}</label>
                <div className="relative">
                  <Link2 className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                  <input type="url" value={formData.agentLogoLink} onChange={(e) => updateFormData('agentLogoLink', e.target.value)} className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder={t("Paste agent's or agency's logo link", "Collez le lien du logo agent/agence")} />
                </div>
              </div>
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-3">{t("Animation Type *", "Type d'animation *")}</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['fade', 'grow', 'shrink', 'move-scale'].map(type => (
                    <button key={type} onClick={() => updateFormData('logoAnimationType', type)} className={`p-3 md:p-4 rounded-xl border-2 transition-all ${formData.logoAnimationType === type ? 'border-[#3C59B2] bg-blue-50 shadow-lg' : 'border-gray-200 hover:border-gray-300'}`}>
                      <div className={`font-bold text-sm md:text-base mb-2 py-2 px-4 rounded-lg ${formData.logoAnimationType === type ? 'bg-[#3C59B2] text-white' : 'bg-gray-100 text-gray-900'}`}>
                        {type === 'fade' && t('Fade in + Fade out', 'Fondu entrée + sortie')}
                        {type === 'grow' && t('Grow in + Fade out', 'Agrandissement + fondu sortie')}
                        {type === 'shrink' && t('Shrink in + Fade out', 'Réduction + fondu sortie')}
                        {type === 'move-scale' && t('Move & Scale in + Fade out', 'Déplacement et zoom + fondu sortie')}
                      </div>
                      <div className="w-full aspect-video rounded-lg overflow-hidden">
                        <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${type === 'fade' ? 'rrNGrN4vRg4' : type === 'grow' ? 'KF7Xia38J0s' : type === 'shrink' ? 'YNextYhidRE' : 'WNqoGWIbD-A'}`} frameBorder="0" allowFullScreen></iframe>
                      </div>
                    </button>
                  ))}
                  <button onClick={() => updateFormData('logoAnimationType', 'none')} className={`p-3 md:p-4 rounded-xl border-2 transition-all md:col-span-2 ${formData.logoAnimationType === 'none' ? 'border-gray-500 bg-gray-50 shadow-lg' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className={`font-bold text-sm md:text-base py-2 px-4 rounded-lg ${formData.logoAnimationType === 'none' ? 'bg-gray-500 text-white' : 'bg-gray-100 text-gray-900'}`}>{t("None (No Animation)", "Aucune (pas d'animation)")}</div>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div>
          <label className="block text-base font-semibold text-gray-700 mb-3">{t("Do you want a title? *", "Voulez-vous un titre ? *")}</label>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <button onClick={() => updateFormData('hasTitle', 'yes')} className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${formData.hasTitle === 'yes' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>{t("Yes", "Oui")}</button>
            <button onClick={() => updateFormData('hasTitle', 'no')} className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${formData.hasTitle === 'no' ? 'border-gray-500 bg-gray-50 text-gray-700' : 'border-gray-200 hover:border-gray-300'}`}>{t("No", "Non")}</button>
          </div>
          {formData.hasTitle === 'yes' && (
            <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">{t("Title Text *", "Texte du titre *")}</label>
              <textarea value={formData.titleText} onChange={(e) => updateFormData('titleText', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" rows="2" placeholder={t("Enter title text", "Saisissez le texte du titre")} />
            </div>
          )}
        </div>
        
        <div>
          <label className="block text-base font-semibold text-gray-700 mb-3">{t("Would you like licensed music? *", "Souhaitez-vous de la musique sous licence ? *")}</label>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <button onClick={() => updateFormData('hasMusic', 'yes')} className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${formData.hasMusic === 'yes' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>{t("Yes", "Oui")}</button>
            <button onClick={() => updateFormData('hasMusic', 'no')} className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${formData.hasMusic === 'no' ? 'border-gray-500 bg-gray-50 text-gray-700' : 'border-gray-200 hover:border-gray-300'}`}>{t("No", "Non")}</button>
          </div>
          {formData.hasMusic === 'yes' && (
            <div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                <div className="flex items-start gap-3">
                  <Music className="w-5 h-5 text-[#3C59B2] flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium text-gray-900 mb-3">{t("Music library:", "Bibliothèque musicale :")}</p>
                    <div className="mt-1">
                      <MusicPicker
                        value={formData.musicTrack}
                        isFR={isFR}
                        onChange={(id) => updateFormData('musicTrack', id !== null ? String(id) : '')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className={`space-y-4 md:space-y-6 ${isFormDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4 mb-4">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center bg-[#3C59B2] flex-shrink-0"><span className="text-xl md:text-2xl font-bold text-white">2</span></div>
        <div><h2 className="text-xl md:text-2xl font-bold text-gray-900">360° Showcase Video</h2></div>
      </div>
      
      <div className="space-y-4 md:space-y-6">
        
        {/* ✅ TEMPLATE TOGGLE */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-4 md:p-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Do you want to apply a template to this 360° Showcase Video? *</label>
          <p className="text-xs text-gray-600 mb-4">Templates include property details, agent info, pricing, etc.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => updateFormData('showcaseApplyTemplate', 'yes')} className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${formData.showcaseApplyTemplate === 'yes' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>Yes, apply template</button>
            <button onClick={() => updateFormData('showcaseApplyTemplate', 'no')} className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${formData.showcaseApplyTemplate === 'no' ? 'border-gray-500 bg-gray-50 text-gray-700' : 'border-gray-200 hover:border-gray-300'}`}>No, skip template</button>
          </div>
        </div>

        {/* ✅ TEMPLATE FIELDS - Only show if "yes" */}
        {formData.showcaseApplyTemplate === 'yes' && (
          <>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Label *</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[{id: 'for-sale', name: 'For Sale', vid: 'Iq32ykXAS7I'}, {id: 'just-listed', name: 'Just Listed', vid: 'K7-3g89gaJU'}].map(label => (
                  <button key={label.id} onClick={() => updateFormData('showcaseLabel', label.id)} className={`p-3 md:p-4 rounded-xl border-2 transition-all ${formData.showcaseLabel === label.id ? 'border-[#3C59B2] bg-blue-50 shadow-lg' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className={`font-bold text-base md:text-lg mb-2 md:mb-3 py-2 px-4 rounded-lg ${formData.showcaseLabel === label.id ? 'bg-[#3C59B2] text-white' : 'bg-gray-100 text-gray-900'}`}>{label.name}</div>
                    <div className="w-full aspect-video rounded-lg overflow-hidden"><iframe className="w-full h-full" src={`https://www.youtube.com/embed/${label.vid}`} frameBorder="0" allowFullScreen></iframe></div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 md:p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Template Information *</h3>
              <div className="space-y-4">
                <input type="text" value={formData.showcasePrice} onChange={(e) => updateFormData('showcasePrice', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Price (e.g., $750,000) *" />
                <div className="relative"><Link2 className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" /><input type="url" value={formData.showcaseAgentPhoto} onChange={(e) => updateFormData('showcaseAgentPhoto', e.target.value)} className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Agent photo link (1080x1080) *" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input type="text" value={formData.showcaseAgentName} onChange={(e) => updateFormData('showcaseAgentName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Agent name *" />
                  <input type="tel" value={formData.showcaseAgentPhone} onChange={(e) => updateFormData('showcaseAgentPhone', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Agent phone *" />
                </div>
                <input type="text" value={formData.showcaseAddress} onChange={(e) => updateFormData('showcaseAddress', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Address *" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <input type="text" value={formData.showcaseCity} onChange={(e) => updateFormData('showcaseCity', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="City *" />
                  <input type="text" value={formData.showcaseState} onChange={(e) => updateFormData('showcaseState', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="State *" />
                  <input type="text" value={formData.showcasePostalCode} onChange={(e) => updateFormData('showcasePostalCode', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="ZIP *" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <input type="number" value={formData.showcaseBedrooms} onChange={(e) => updateFormData('showcaseBedrooms', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Bedrooms *" />
                  <input type="number" value={formData.showcaseBathrooms} onChange={(e) => updateFormData('showcaseBathrooms', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Bathrooms *" />
                  <input type="number" value={formData.showcaseSqFt} onChange={(e) => updateFormData('showcaseSqFt', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Sq Ft *" />
                </div>
              </div>
            </div>
          </>
        )}

        {/* ✅ NO TEMPLATE MESSAGE */}
        {formData.showcaseApplyTemplate === 'no' && (
          <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4 md:p-6">
            <div className="flex items-center gap-3">
              <Check className="w-6 h-6 text-green-600" />
              <p className="text-sm text-gray-700">No template will be applied. The video will be created without property details overlay.</p>
            </div>
          </div>
        )}
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Music? *</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => updateFormData('showcaseHasMusic', 'yes')} className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${formData.showcaseHasMusic === 'yes' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>Yes</button>
            <button onClick={() => updateFormData('showcaseHasMusic', 'no')} className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${formData.showcaseHasMusic === 'no' ? 'border-gray-500 bg-gray-50 text-gray-700' : 'border-gray-200 hover:border-gray-300'}`}>No</button>
          </div>
          {formData.showcaseHasMusic === 'yes' && (
            <div className="mt-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                <div className="flex items-start gap-3">
                  <Music className="w-5 h-5 text-[#3C59B2] flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium text-gray-900 mb-3">Music library:</p>
                    <div className="mt-1">
                      <MusicPicker
                        value={formData.showcaseMusicTrack}
                        isFR={isFR}
                        onChange={(id) => updateFormData('showcaseMusicTrack', id !== null ? String(id) : '')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className={`space-y-4 md:space-y-6 ${isFormDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4 mb-4">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center bg-[#3C59B2] flex-shrink-0"><span className="text-xl md:text-2xl font-bold text-white">3</span></div>
        <div><h2 className="text-xl md:text-2xl font-bold text-gray-900">Premium Slideshow</h2><p className="text-xs md:text-sm text-gray-600">30 photos max</p></div>
      </div>
      <div className="space-y-4 md:space-y-6">
        <div className="relative"><Link2 className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" /><input type="url" value={formData.premiumPhotosLink} onChange={(e) => updateFormData('premiumPhotosLink', e.target.value)} className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Link to 30 photos (Dropbox, Google Drive, etc.) *" /></div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Photos numbered 1-30? *</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => updateFormData('premiumPhotosNumbered', 'yes')} className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${formData.premiumPhotosNumbered === 'yes' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>Yes</button>
            <button onClick={() => updateFormData('premiumPhotosNumbered', 'no')} className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${formData.premiumPhotosNumbered === 'no' ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 hover:border-gray-300'}`}>No</button>
          </div>
        </div>
        <div className={formData.premiumPhotosNumbered === 'no' ? 'opacity-50 pointer-events-none' : ''}>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Transition *</label>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 items-center">
              <div className="w-full aspect-video rounded-lg overflow-hidden"><iframe className="w-full h-full" src="https://www.youtube.com/embed/xv59KUtu8xM" frameBorder="0" allowFullScreen></iframe></div>
              <div className="grid grid-cols-2 gap-2">
                {['Transition 1', 'Transition 2', 'Transition 3', 'Transition 4'].map(t => (
                  <button key={t} onClick={() => updateFormData('premiumSlideshowTransition', t)} className={`py-2 md:py-3 px-2 rounded-lg border-2 text-xs md:text-sm font-medium transition-all flex items-center justify-center ${formData.premiumSlideshowTransition === t ? 'border-[#3C59B2] bg-[#3C59B2] text-white' : 'border-white bg-white hover:border-[#3C59B2]'}`}>{t}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 md:mt-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">Music? *</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => updateFormData('premiumHasMusic', 'yes')} className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${formData.premiumHasMusic === 'yes' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>Yes</button>
              <button onClick={() => updateFormData('premiumHasMusic', 'no')} className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${formData.premiumHasMusic === 'no' ? 'border-gray-500 bg-gray-50 text-gray-700' : 'border-gray-200 hover:border-gray-300'}`}>No</button>
            </div>
            {formData.premiumHasMusic === 'yes' && (
              <div className="mt-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                  <div className="flex items-start gap-3">
                    <Music className="w-5 h-5 text-[#3C59B2] flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-medium text-gray-900 mb-3">Music library:</p>
                      <div className="mt-1">
                        <MusicPicker
                          value={formData.premiumMusicTrack}
                          isFR={isFR}
                          onChange={(id) => updateFormData('premiumMusicTrack', id !== null ? String(id) : '')}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className={`space-y-4 md:space-y-6 ${isFormDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4 mb-4">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center bg-[#3C59B2] flex-shrink-0"><span className="text-xl md:text-2xl font-bold text-white">4</span></div>
        <div><h2 className="text-xl md:text-2xl font-bold text-gray-900">Short Success Slideshow</h2><p className="text-xs md:text-sm text-gray-600">7 photos max</p></div>
      </div>
      
      <div className="space-y-4 md:space-y-6">
        <div className="relative"><Link2 className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" /><input type="url" value={formData.shortPhotosLink} onChange={(e) => updateFormData('shortPhotosLink', e.target.value)} className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Link to 7 photos (Dropbox, Google Drive, etc.) *" /></div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Photos numbered 1-7? *</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => updateFormData('shortPhotosNumbered', 'yes')} className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${formData.shortPhotosNumbered === 'yes' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>Yes</button>
            <button onClick={() => updateFormData('shortPhotosNumbered', 'no')} className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${formData.shortPhotosNumbered === 'no' ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 hover:border-gray-300'}`}>No</button>
          </div>
        </div>

        {/* ✅ TEMPLATE TOGGLE */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-4 md:p-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Do you want to apply a template to this Short Success Slideshow? *</label>
          <p className="text-xs text-gray-600 mb-4">Templates include property details, agent info, pricing, etc.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => updateFormData('shortApplyTemplate', 'yes')} className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${formData.shortApplyTemplate === 'yes' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>Yes, apply template</button>
            <button onClick={() => updateFormData('shortApplyTemplate', 'no')} className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${formData.shortApplyTemplate === 'no' ? 'border-gray-500 bg-gray-50 text-gray-700' : 'border-gray-200 hover:border-gray-300'}`}>No, skip template</button>
          </div>
        </div>

        {/* ✅ TEMPLATE FIELDS - Only show if "yes" */}
        {formData.shortApplyTemplate === 'yes' && (
          <>
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-4 items-center">
                <div className="flex items-center gap-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <p className="text-sm font-semibold text-gray-700">Label: <span className="text-green-700">Sold</span> (automatically applied)</p>
                </div>
                <div className="w-full aspect-video rounded-lg overflow-hidden">
                  <iframe className="w-full h-full" src="https://www.youtube.com/embed/X7EB2kS2hPE" frameBorder="0" allowFullScreen></iframe>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 md:p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Template Information *</h3>
              <div className="space-y-4">
                <input type="text" value={formData.shortPrice} onChange={(e) => updateFormData('shortPrice', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Price (e.g., $750,000) *" />
                <div className="relative"><Link2 className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" /><input type="url" value={formData.shortAgentPhoto} onChange={(e) => updateFormData('shortAgentPhoto', e.target.value)} className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Agent photo link (1080x1080) *" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input type="text" value={formData.shortAgentName} onChange={(e) => updateFormData('shortAgentName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Agent name *" />
                  <input type="tel" value={formData.shortAgentPhone} onChange={(e) => updateFormData('shortAgentPhone', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Agent phone *" />
                </div>
                <input type="text" value={formData.shortAddress} onChange={(e) => updateFormData('shortAddress', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Address *" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <input type="text" value={formData.shortCity} onChange={(e) => updateFormData('shortCity', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="City *" />
                  <input type="text" value={formData.shortState} onChange={(e) => updateFormData('shortState', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="State *" />
                  <input type="text" value={formData.shortPostalCode} onChange={(e) => updateFormData('shortPostalCode', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="ZIP *" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <input type="number" value={formData.shortBedrooms} onChange={(e) => updateFormData('shortBedrooms', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Bedrooms *" />
                  <input type="number" value={formData.shortBathrooms} onChange={(e) => updateFormData('shortBathrooms', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Bathrooms *" />
                  <input type="number" value={formData.shortSqFt} onChange={(e) => updateFormData('shortSqFt', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-sm md:text-base" placeholder="Sq Ft *" />
                </div>
              </div>
            </div>
          </>
        )}

        {/* ✅ NO TEMPLATE MESSAGE */}
        {formData.shortApplyTemplate === 'no' && (
          <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4 md:p-6">
            <div className="flex items-center gap-3">
              <Check className="w-6 h-6 text-green-600" />
              <p className="text-sm text-gray-700">No template will be applied. The slideshow will be created without property details overlay.</p>
            </div>
          </div>
        )}
        
        <div className={formData.shortPhotosNumbered === 'no' ? 'opacity-50 pointer-events-none' : ''}>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Transition *</label>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 items-center">
              <div className="w-full aspect-video rounded-lg overflow-hidden"><iframe className="w-full h-full" src="https://www.youtube.com/embed/xv59KUtu8xM" frameBorder="0" allowFullScreen></iframe></div>
              <div className="grid grid-cols-2 gap-2">
                {['Transition 1', 'Transition 2', 'Transition 3', 'Transition 4'].map(t => (
                  <button key={t} onClick={() => updateFormData('shortSlideshowTransition', t)} className={`py-2 md:py-3 px-2 rounded-lg border-2 text-xs md:text-sm font-medium transition-all flex items-center justify-center ${formData.shortSlideshowTransition === t ? 'border-[#3C59B2] bg-[#3C59B2] text-white' : 'border-white bg-white hover:border-[#3C59B2]'}`}>{t}</button>
                ))}
              </div>
            </div>
          </div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Music? *</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => updateFormData('shortHasMusic', 'yes')} className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${formData.shortHasMusic === 'yes' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>Yes</button>
            <button onClick={() => updateFormData('shortHasMusic', 'no')} className={`flex-1 py-2 px-4 rounded-lg border-2 font-medium transition-all ${formData.shortHasMusic === 'no' ? 'border-gray-500 bg-gray-50 text-gray-700' : 'border-gray-200 hover:border-gray-300'}`}>No</button>
          </div>
          {formData.shortHasMusic === 'yes' && (
            <div className="mt-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                <div className="flex items-start gap-3">
                  <Music className="w-5 h-5 text-[#3C59B2] flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium text-gray-900 mb-3">Music library:</p>
                    <div className="mt-1">
                      <MusicPicker
                        value={formData.shortMusicTrack}
                        isFR={isFR}
                        onChange={(id) => updateFormData('shortMusicTrack', id !== null ? String(id) : '')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className={`space-y-5 md:space-y-7 ${isFormDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4 mb-4">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center bg-[#3C59B2] flex-shrink-0">
          <span className="text-xl md:text-2xl font-bold text-white">3</span>
        </div>
        <div className="flex-1">
          <h2 className="font-manrope text-2xl md:text-3xl font-bold text-gray-900 mb-3 animate-fade-in-up">
            {t("Final Details", "Détails finaux")}
          </h2>
          <p className="text-base md:text-lg text-gray-600">
            {t("Last step before submission", "Dernière étape avant envoi")}
          </p>
        </div>
      </div>
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-4 md:p-6">
        <label className="block text-base font-semibold text-gray-700 mb-3">{t("Would you grant us permission to use your videos in our portfolio? *", "Autorisez-vous l'utilisation de vos vidéos dans notre portfolio ? *")}</label>
        <p className="text-sm text-gray-600 mb-4">{t("This allows us to showcase our work to potential clients.", "Cela nous permet de présenter notre travail à de futurs clients.")}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={() => updateFormData('portfolioPermission', 'yes')} className={`flex-1 py-3 px-4 rounded-full border-2 font-medium transition-all ${formData.portfolioPermission === 'yes' ? 'border-[#3C59B2] bg-blue-50 text-[#3C59B2]' : 'border-gray-200 hover:border-gray-300'}`}>{t("Yes", "Oui")}</button>
          <button onClick={() => updateFormData('portfolioPermission', 'no')} className={`flex-1 py-3 px-4 rounded-full border-2 font-medium transition-all ${formData.portfolioPermission === 'no' ? 'border-gray-500 bg-gray-50 text-gray-700' : 'border-gray-200 hover:border-gray-300'}`}>{t("No", "Non")}</button>
        </div>
      </div>
      <div>
        <label className="block text-base font-semibold text-gray-700 mb-2">{t("Additional Notes", "Avez-vous une demande particulière pour cette vidéo ?")}</label>
        <textarea
          value={formData.notes}
          onChange={(e) => updateFormData('notes', e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-base md:text-lg"
          rows="6"
        />
      </div>

      <div>
        <p className="block text-base font-semibold text-gray-700 mb-2">
          {t(
            "We believe a good product is built with its customers. We read everything and move forward with you.",
            "Nous croyons qu'un bon produit se construit avec ses clients. On lit tout et on avance avec vous."
          )}
        </p>
        <textarea
          value={formData.extraFeedback}
          onChange={(e) => updateFormData('extraFeedback', e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none text-base md:text-lg"
          rows="4"
          placeholder={t(
            "Share your ideas, feedback, or anything you feel is missing.",
            "Partagez vos idées, vos retours ou ce qui manque selon vous."
          )}
        />
      </div>

      <div className="card-hover bg-white border-2 border-[#3C59B2] rounded-xl p-6 shadow-lg">
        <div className="flex items-start gap-4">
          <div className="bg-blue-50 p-2 rounded-full">
            <Mail className="w-6 h-6 text-[#3C59B2]" />
          </div>
          <div className="flex-1">
            <label className="block text-lg font-bold text-gray-900 mb-2">
              {t("Where should we send the finished videos?", "Où envoyer les vidéos finalisées ?")} <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-600 mb-3">
              {t("Confirm your email address or enter a different one (e.g., your assistant's).", "Confirmez votre email ou indiquez une autre adresse (ex. celle de votre assistant).")}
            </p>
            <input
              type="email"
              required
              value={formData.deliveryEmail}
              onChange={(e) => updateFormData('deliveryEmail', e.target.value)}
              placeholder="recipient@example.com"
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-[#3C59B2] outline-none transition-all"
            />
          </div>
        </div>
      </div>

      {!isFR && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs md:text-sm text-amber-900">
            {t(
              "Orders placed Monday-Thursday delivered within 24 hours when possible. Friday-Sunday orders are processed starting Monday.",
              "Commandes du lundi au jeudi livrées sous 24 h lorsque possible. Commandes vendredi-dimanche traitées à partir du lundi."
            )}
          </p>
        </div>
      )}
    </div>
  );

  const steps = [renderStep1, renderStep2, renderStep6];
  const stepTitles = [t('Property Info', 'Infos propriété'), t('Walkthrough', 'Composition de la vidéo'), t('Review', 'Récapitulatif')];
  const totalSteps = 3;
  const progress = (currentStep / totalSteps) * 100;

  const handleNext = () => {
    const errors = validateStep(currentStep === 3 ? 6 : currentStep);
    if (errors.length > 0) {
      setValidationErrors(errors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setValidationErrors([]);
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      setTimeout(() => {
        if (stepTitleRef.current) {
          const elementPosition = stepTitleRef.current.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 20;
          window.scrollTo({top: offsetPosition, behavior: 'smooth'});
        }
      }, 100);
    }
  };

  const handleBack = () => {
    setValidationErrors([]);
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setTimeout(() => {
        if (stepTitleRef.current) {
          const elementPosition = stepTitleRef.current.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 20;
          window.scrollTo({top: offsetPosition, behavior: 'smooth'});
        }
      }, 100);
    }
  };

  const handleSubmit = async () => {
    const errors = validateStep(6);
    if (errors.length > 0) {
      setValidationErrors(errors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setValidationErrors([]);
    
    if (isFormDisabled) {
      alert(t('Cannot submit: Service only for residential properties', 'Envoi impossible : service réservé aux propriétés résidentielles'));
      return;
    }
    
    setIsSubmitting(true);
    await onSubmit(formData);
  };

  return (
    <div className="font-manrope min-h-screen bg-white p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-4 md:mb-6" ref={stepTitleRef}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-manrope text-base md:text-lg font-bold text-gray-900">{stepTitles[currentStep - 1]}</h3>
              <p className="text-xs md:text-sm text-gray-600">{t("Step", "Étape")} {currentStep} {t("of", "sur")} {totalSteps}</p>
            </div>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#3C59B2] to-[#2A4491] transition-all duration-300" style={{width: `${progress}%`}} />
          </div>
        </div>

        {currentStep > 1 && currentStep < totalSteps && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-4 md:p-6 rounded-3xl border border-[#E2E8F0] mb-6 md:mb-8">
            <button onClick={handleBack} className="btn-premium overflow-hidden w-full sm:w-auto flex items-center justify-center gap-2 px-6 md:px-10 py-3 md:py-4 rounded-full font-bold transition-all border-2 bg-white text-gray-700 hover:bg-gray-50 border-gray-300">
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
              <span className="text-base md:text-lg">{t("Back", "Retour")}</span>
            </button>
            <button onClick={handleNext} disabled={isFormDisabled} className={`btn-premium overflow-hidden w-full sm:w-auto flex items-center justify-center gap-2 px-6 md:px-10 py-3 md:py-4 rounded-full font-bold text-white transition-all border-2 ${isFormDisabled ? 'opacity-50 cursor-not-allowed bg-[#3C59B2] border-[#3C59B2]' : 'bg-[#3C59B2] hover:bg-[#2d4a8f] border-[#3C59B2]'}`}>
              <span className="text-base md:text-lg">{t("Next", "Suivant")}</span>
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>
        )}

        <div className="card-hover bg-white border border-[#E2E8F0] rounded-3xl p-4 md:p-8 mb-4 md:mb-6">
          {renderValidationErrors()}
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep6()}
        </div>

        <div className="card-hover flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-4 md:p-6 rounded-3xl border border-[#E2E8F0]">
          <button onClick={handleBack} disabled={currentStep === 1} className={`btn-premium overflow-hidden w-full sm:w-auto flex items-center justify-center gap-2 px-6 md:px-10 py-3 md:py-4 rounded-full font-bold transition-all border-2 ${currentStep === 1 ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-[#3C59B2] border-2 border-[#3C59B2] hover:bg-slate-50'}`}>
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            <span className="text-base md:text-lg">{t("Back", "Retour")}</span>
          </button>
          
          {currentStep < totalSteps ? (
            <button onClick={handleNext} disabled={isFormDisabled} className={`btn-premium overflow-hidden w-full sm:w-auto flex items-center justify-center gap-2 px-6 md:px-10 py-3 md:py-4 rounded-full font-bold text-white transition-all border-2 ${isFormDisabled ? 'opacity-50 cursor-not-allowed bg-[#3C59B2] border-[#3C59B2]' : 'bg-[#3C59B2] hover:bg-[#2d4a8f] border-[#3C59B2]'}`}>
              <span className="text-base md:text-lg">{t("Next", "Suivant")}</span>
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={isFormDisabled || isSubmitting} className={`btn-premium overflow-hidden w-full sm:w-auto flex items-center justify-center gap-2 px-6 md:px-10 py-3 md:py-4 rounded-full font-bold text-white transition-all border-2 transform hover:scale-105 shadow-lg ${isFormDisabled || isSubmitting ? 'opacity-50 cursor-not-allowed bg-[#3C59B2] border-[#3C59B2]' : 'bg-[#3C59B2] hover:bg-[#2d4a8f] border-[#3C59B2]'}`}>
              <Check className="w-5 h-5 md:w-6 md:h-6" />
              <span className="text-base md:text-lg">{isSubmitting ? `⏳ ${t('Sending...', 'Envoi en cours...')}` : t('Submit Order', 'Envoyer la commande')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewOrderForm;